import { useState, useEffect } from 'react'
import { OrdersAPI, UsersAPI, HostingsAPI, ComplementsAPI } from '../api'
import { Search, Filter, Edit, Trash2, Users, Plus, ChevronRight, Users as UsersIcon, Eye, EyeOff } from 'lucide-react'
import { Toggle } from './Toggle'
import { RichEditor } from './RichEditor'
import { useNavigate } from 'react-router-dom'
import { useAdmin } from '../lib/hooks'
import { Pagination } from './Pagination'
import { useAuth } from '@clerk/clerk-react'
import { Modal } from './Modal'

const AdminOrders = () => {
  const { isAdmin, loading: adminLoading } = useAdmin()
  const navigate = useNavigate()
  const { getToken } = useAuth()

  const [items, setItems] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('ALL')
  const [userFilter, setUserFilter] = useState<string>('ALL')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [editingOrder, setEditingOrder] = useState<any>(null)
  const [deletingOrder, setDeletingOrder] = useState<any>(null)
  const [newStatus, setNewStatus] = useState<string>('')
  const [newDomain, setNewDomain] = useState<string>('')
  const [newDate, setNewDate] = useState<string>('')
  const [newNotes, setNewNotes] = useState<string>('')
  const [newQuantity, setNewQuantity] = useState<number>(1)
  const [newUserId, setNewUserId] = useState<string>('')
  const [newPaymentMethod, setNewPaymentMethod] = useState<string>('')
  const [sendEmail, setSendEmail] = useState(false)
  const [editCpanelUrl, setEditCpanelUrl] = useState('')
  const [editCpanelUser, setEditCpanelUser] = useState('')
  const [editCpanelPassword, setEditCpanelPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [credentialsLoading, setCredentialsLoading] = useState(false)

  const [users, setUsers] = useState<any[]>([])
  const [hostings, setHostings] = useState<any[]>([])
  const [complements, setComplements] = useState<any[]>([])
  const [creating, setCreating] = useState(false)

  // Create form state
  const [createData, setCreateData] = useState({
    userId: '',
    type: 'hosting', // hosting | complement
    hostingId: '',
    complementId: '',
    quantity: 1,
    domain: '',
    status: 'ACTIVE',
    date: new Date().toISOString().split('T')[0],
    lastPaymentMethod: '',
    sendEmail: false
  })

  // Transfer modal state
  const [transferringOrder, setTransferringOrder] = useState<any>(null)
  const [transferUserId, setTransferUserId] = useState<string>('')

  const load = async () => {
    setLoading(true)
    try {
      const [ordersRes, usersRes, hostingsRes, complementsRes] = await Promise.all([
        OrdersAPI.list(),
        UsersAPI.getAll(),
        HostingsAPI.list(),
        ComplementsAPI.list()
      ])

      setItems(ordersRes.data || [])
      setUsers(usersRes.data || [])
      setHostings(hostingsRes.data || [])
      setComplements(complementsRes.data || [])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isAdmin) load()
  }, [isAdmin])

  const handleDelete = (order: any) => {
    setDeletingOrder(order)
    setSendEmail(true)
  }

  const handleTransferClick = (order: any) => {
    setTransferringOrder(order)
    setTransferUserId(order.userId || '')
  }

  const confirmDelete = async () => {
    if (!deletingOrder) return

    await OrdersAPI.delete(deletingOrder.id)

    if (sendEmail && deletingOrder.user && (deletingOrder.hosting || deletingOrder.complement)) {
      const svc = deletingOrder.hosting || deletingOrder.complement;
      await OrdersAPI.sendStatusEmail({
        userEmail: deletingOrder.user.email,
        userName: `${deletingOrder.user.firstName || ''} ${deletingOrder.user.lastName || ''}`.trim(),
        planName: svc.name || svc.displayName,
        planPrice: String(svc.price),
        planCurrency: svc.currency,
        planPeriod: svc.payType,
        type: 'DELETED'
      })
    }

    setDeletingOrder(null)
    load()
  }

  const handleEdit = (order: any) => {
    setEditingOrder(order)
    setNewStatus(order.status)
    setNewDomain(order.domain || '')
    setNewNotes(order.notes || '')
    setNewQuantity(order.quantity || 1)
    setNewUserId(order.userId || '')
    setNewPaymentMethod(order.lastPaymentMethod || '')

    if (order.createdAt) {
      const date = new Date(order.createdAt)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      setNewDate(`${year}-${month}-${day}`)
    } else {
      setNewDate('')
    }

    setSendEmail(true)

    // Load cPanel credentials
    setEditCpanelUrl('')
    setEditCpanelUser('')
    setEditCpanelPassword('')
    setShowPassword(false)
    setCredentialsLoading(true)
    getToken().then(token => {
      fetch(`/api/hosting-credentials?id=${order.id}`, {
        headers: { Authorization: `Bearer ${token || ''}` },
      })
        .then(r => r.json())
        .then(data => {
          if (data.success && data.data) {
            setEditCpanelUrl(data.data.cpanelUrl || '')
            setEditCpanelUser(data.data.cpanelUser || '')
            setEditCpanelPassword(data.data.cpanelPassword || '')
          }
        })
        .catch(() => {})
        .finally(() => setCredentialsLoading(false))
    })
  }

  const handleUpdate = async () => {
    if (editingOrder) {
      const updateData: any = {
        status: newStatus,
        domain: newDomain,
        notes: newNotes || null,
        quantity: newQuantity,
        lastPaymentMethod: newPaymentMethod || null
      }

      // Traspaso de usuario
      if (newUserId && newUserId !== editingOrder.userId) {
        updateData.userId = newUserId
      }

      let dateChanged = false
      if (newDate) {
        // Detectar si cambió la fecha
        if (editingOrder.createdAt) {
          const date = new Date(editingOrder.createdAt)
          const year = date.getFullYear()
          const month = String(date.getMonth() + 1).padStart(2, '0')
          const day = String(date.getDate()).padStart(2, '0')
          const oldDateStr = `${year}-${month}-${day}`
          if (newDate !== oldDateStr) dateChanged = true
        } else {
          dateChanged = true
        }

        const [y, m, d] = newDate.split('-').map(Number)
        updateData.createdAt = new Date(y, m - 1, d)
      }

      await OrdersAPI.update(editingOrder.id, updateData)

      // Save cPanel credentials via secure endpoint
      try {
        const token = await getToken()
        await fetch('/api/hosting-credentials', {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token || ''}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: editingOrder.id,
            cpanelUrl: editCpanelUrl,
            cpanelUser: editCpanelUser,
            cpanelPassword: editCpanelPassword,
          }),
        })
      } catch (credErr) {
        console.error('Error saving cPanel credentials:', credErr)
      }

      if (sendEmail && editingOrder.user && (editingOrder.hosting || editingOrder.complement)) {
        const svc = editingOrder.hosting || editingOrder.complement;
        const statusChanged = editingOrder.status !== newStatus
        let emailType: 'STATUS_CHANGE' | 'DATE_CHANGE' = 'STATUS_CHANGE'
        let renewalDateStr: string | undefined

        if (!statusChanged && dateChanged) {
          emailType = 'DATE_CHANGE'

          if (newDate) {
            const [y, m, d] = newDate.split('-').map(Number)
            const startDate = new Date(y, m - 1, d)
            const renewalDate = new Date(startDate)

            const payType = svc.payType

            if (payType === 'ANUAL') {
              renewalDate.setFullYear(renewalDate.getFullYear() + 1)
            } else if (payType === 'MENSUAL') {
              renewalDate.setMonth(renewalDate.getMonth() + 1)
            }

            renewalDateStr = renewalDate.toLocaleDateString('es-AR', {
              day: '2-digit',
              month: 'long',
              year: 'numeric'
            })
          }
        }

        await OrdersAPI.sendStatusEmail({
          userEmail: editingOrder.user.email,
          userName: `${editingOrder.user.firstName || ''} ${editingOrder.user.lastName || ''}`.trim(),
          planName: svc.name || svc.displayName,
          planPrice: String(svc.price),
          planCurrency: svc.currency,
          planPeriod: svc.payType,
          status: newStatus,
          renewalDate: renewalDateStr,
          type: emailType
        })
      }

      setEditingOrder(null)
      load()
    }
  }

  const handleCreate = async () => {
    if (!createData.userId) {
      alert('Por favor selecciona un usuario')
      return
    }

    if (createData.type === 'hosting' && !createData.hostingId) {
      alert('Por favor selecciona un plan de hosting')
      return
    }

    if (createData.type === 'complement' && !createData.complementId) {
      alert('Por favor selecciona un complemento')
      return
    }

    try {
      await OrdersAPI.createAsAdmin({
        userId: createData.userId,
        hostingId: createData.type === 'hosting' ? createData.hostingId : undefined,
        complementId: createData.type === 'complement' ? createData.complementId : undefined,
        quantity: createData.type === 'complement' ? createData.quantity : 1,
        domain: createData.domain,
        status: createData.status,
        createdAt: createData.date,
        lastPaymentMethod: createData.lastPaymentMethod
      })

      setCreating(false)

      if (createData.sendEmail) {
        const user = users.find(u => u.id === createData.userId)
        const item = createData.type === 'hosting'
          ? hostings.find(h => h.id === createData.hostingId)
          : complements.find(c => c.id === createData.complementId)

        if (user && item) {
          const planName = createData.type === 'hosting' ? item.name : item.displayName
          const quantitySuffix = (createData.quantity > 1) ? ` (x${createData.quantity})` : ''

          await OrdersAPI.sendStatusEmail({
            userEmail: user.email,
            userName: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
            planName: planName + quantitySuffix,
            planPrice: String(item.price * createData.quantity),
            planCurrency: item.currency,
            planPeriod: item.payType || (item.payTypeDisplay || (item.payType === 'ANUAL' ? 'Anual' : 'Mensual')),
            status: createData.status,
            type: 'STATUS_CHANGE'
          })
        }
      }

      // Reset form
      setCreateData({
        userId: '',
        type: 'hosting',
        hostingId: '',
        complementId: '',
        quantity: 1,
        domain: '',
        status: 'ACTIVE',
        date: new Date().toISOString().split('T')[0],
        lastPaymentMethod: '',
        sendEmail: false
      })
      load()
    } catch (error) {
      console.error('Error creating order:', error)
      alert('Error al crear la orden')
    }
  }

  if (adminLoading) return <div className="min-h-[300px] flex items-center justify-center"><p className="text-gray-600">Cargando permisos...</p></div>
  if (!isAdmin) return <div className="min-h-[300px] flex items-center justify-center"><p className="text-gray-600">Acceso restringido</p></div>

  const uniqueUsers = Array.from(new Map(items.map(item => [item.user?.id, item.user])).values())
    .filter(u => u && u.id)
    .sort((a: any, b: any) => {
      const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim()
      const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim()
      return nameA.localeCompare(nameB)
    })

  const filtered = items.filter((it) => {
    const q = query.trim().toLowerCase()
    const svcName = it.hosting?.name || it.complement?.displayName || ''
    const matchesQuery = q ? (
      it.id.toLowerCase().includes(q) ||
      it.user?.email?.toLowerCase().includes(q) ||
      it.user?.firstName?.toLowerCase().includes(q) ||
      svcName.toLowerCase().includes(q)
    ) : true
    const matchesStatus = statusFilter === 'ALL' ? true : it.status === statusFilter
    const matchesUser = userFilter === 'ALL' ? true : it.user?.id === userFilter
    return matchesQuery && matchesStatus && matchesUser
  })

  // const total = items.length
  const filteredTotal = filtered.length
  const startIdx = (page - 1) * pageSize
  const endIdx = Math.min(startIdx + pageSize, filteredTotal)
  const pageItems = filtered.slice(startIdx, endIdx)

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING': return 'Pendiente de Pago'
      case 'PAID': return 'Pago'
      case 'ACTIVE': return 'Activo'
      case 'CANCELLED': return 'Cancelado'
      default: return status
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return 'bg-yellow-100 text-yellow-800'
      case 'PAID': return 'bg-green-100 text-green-800'
      case 'ACTIVE': return 'bg-blue-100 text-blue-800'
      case 'CANCELLED': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-24 mb-16">
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl md:text-3xl font-bold dark:text-white">Administración de Órdenes</h2>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/admin')}
              className="px-4 py-2 rounded-md bg-gray-100 text-gray-700 shadow-sm hover:bg-gray-200 transition-colors dark:bg-slate-800 dark:text-gray-200 dark:hover:bg-slate-700"
            >
              Volver a Hostings
            </button>
            <button
              onClick={() => setCreating(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 text-white shadow-sm hover:bg-blue-700 transition-colors"
            >
              <Plus size={18} />
              Nueva Orden
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[240px]">
            <div className="relative">
              <input
                value={query}
                onChange={(e) => { setQuery(e.target.value); setPageSize(10) }}
                placeholder="Buscar por ID, usuario o plan..."
                className="w-full border rounded-md px-3 py-2 pl-9 dark:bg-slate-800 dark:border-slate-700 dark:text-white dark:placeholder-gray-400"
              />
              <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-500" />
            <select
              className="border rounded-md px-3 py-2 max-w-[200px] dark:bg-slate-800 dark:border-slate-700 dark:text-white"
              value={userFilter}
              onChange={(e) => { setUserFilter(e.target.value); setPage(1) }}
            >
              <option value="ALL">Todos los clientes</option>
              {uniqueUsers.map((u: any) => (
                <option key={u.id} value={u.id}>
                  {u.firstName} {u.lastName} ({u.email})
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <select
              className="border rounded-md px-3 py-2 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
            >
              <option value="ALL">Todos los estados</option>
              <option value="PENDING">Pendiente de Pago</option>
              <option value="PAID">Pago</option>
              <option value="ACTIVE">Activo</option>
              <option value="CANCELLED">Cancelado</option>
            </select>
          </div>
          <div className="text-sm text-gray-500">
            Mostrando {filteredTotal === 0 ? 0 : startIdx + 1}-{endIdx} de {filteredTotal}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden dark:bg-slate-800 dark:border-slate-700">
        {loading ? (
          <div className="px-5 py-8 text-gray-500">Cargando órdenes...</div>
        ) : (
          <div className="px-0 py-0 overflow-x-visible md:px-5 md:py-4 md:overflow-x-auto">
            {/* Mobile View */}
            <div className="md:hidden divide-y divide-gray-100 dark:divide-slate-700">
              {pageItems.map((it) => (
                <div key={it.id} className="p-4 hover:bg-gray-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors" onClick={() => handleEdit(it)}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0 pr-4">
                      {/* Top Row: Date */}
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(it.createdAt)}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(it.status)}`}>
                          {getStatusLabel(it.status)}
                        </span>
                      </div>

                      {/* Bottom Row: Name & Price */}
                      <div className="flex justify-between items-center mt-2">
                        <span className="font-medium text-gray-900 dark:text-white truncate max-w-[60%]">
                          {it.hosting?.name || it.complement?.displayName}{(it.quantity || 1) > 1 ? ` (x${it.quantity})` : ''}
                        </span>
                        <span className="font-bold text-gray-900 dark:text-white whitespace-nowrap">
                          {it.hosting?.currency || it.complement?.currency} {((it.hosting?.price || it.complement?.price || 0) * (it.quantity || 1))}
                        </span>
                      </div>
                    </div>

                    {/* Arrow Icon */}
                    <div className="flex items-center justify-center h-full pt-1">
                      <ChevronRight className="text-gray-400" size={20} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop View */}
            <div className="hidden md:block">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
                <thead className="bg-gray-50 dark:bg-slate-700/50">
                  <tr>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider p-3 dark:text-gray-400">Fecha</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider p-3 dark:text-gray-400">Usuario</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider p-3 dark:text-gray-400">Plan</th>
                    <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider p-3 dark:text-gray-400">Estado</th>
                    <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider p-3 dark:text-gray-400">Precio</th>
                    <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider p-3 dark:text-gray-400">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-700/50">
                  {pageItems.map((it) => (
                    <tr key={it.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                      <td className="p-3">
                        <div className="text-sm text-gray-900 dark:text-white">{formatDate(it.createdAt)}</div>
                      </td>
                      <td className="p-3">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {it.user?.firstName} {it.user?.lastName}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{it.user?.email}</div>
                        <div className={`text-xs mt-1 ${it.domain ? 'text-gray-900 font-medium dark:text-white' : 'text-gray-400 italic dark:text-gray-500'}`}>
                          {it.domain || 'dominio.com'}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="font-medium text-gray-900 dark:text-white">{it.hosting?.name || it.complement?.displayName}{(it.quantity || 1) > 1 ? ` (x${it.quantity})` : ''}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {it.complement ? 'Complemento' : (it.hosting?.payType || '')}
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(it.status)}`}>
                          {getStatusLabel(it.status)}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <span className="text-sm font-medium dark:text-white">
                          {(it.hosting?.currency || it.complement?.currency)} {((it.hosting?.price || it.complement?.price || 0) * (it.quantity || 1))}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => handleTransferClick(it)}
                            className="p-2 text-[#d97706] hover:bg-orange-50 rounded-full transition-colors dark:text-[#fbbf24] dark:hover:bg-orange-900/30"
                            title="Transferir a otro usuario"
                          >
                            <UsersIcon size={18} />
                          </button>
                          <button
                            onClick={() => handleEdit(it)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors dark:text-blue-400 dark:hover:bg-blue-900/30"
                            title="Editar estado"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(it)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors dark:text-red-400 dark:hover:bg-red-900/30"
                            title="Borrar"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Edit Modal */}
            <Modal
              open={!!editingOrder}
              onClose={() => setEditingOrder(null)}
              icon={<Edit size={22} />}
              title="Editar Orden"
              subtitle={`${editingOrder?.hosting?.name || editingOrder?.complement?.displayName || ''}${(editingOrder?.quantity || 1) > 1 ? ` (x${editingOrder?.quantity})` : ''} — ${editingOrder?.user?.email || ''}`}
              footer={
                <>
                  <button
                    onClick={() => setEditingOrder(null)}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md dark:text-gray-300 dark:hover:bg-slate-700"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleUpdate}
                    className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md"
                  >
                    Guardar
                  </button>
                </>
              }
            >
              {editingOrder && (
                <>
                  <div className={`mb-4 grid gap-4 ${(editingOrder.hosting?.name || editingOrder.complement?.displayName || '').toLowerCase().includes('zoho') ? 'grid-cols-2' : 'grid-cols-1'}`}>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Traspasar a usuario</label>
                      <select
                        value={newUserId}
                        onChange={(e) => setNewUserId(e.target.value)}
                        className={`w-full border-2 rounded-md px-3 py-2 dark:bg-slate-700 dark:text-white transition-colors ${newUserId !== editingOrder.userId
                            ? 'border-amber-400 dark:border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                            : 'border-gray-200 dark:border-slate-600'
                          }`}
                      >
                        {users
                          .slice()
                          .sort((a: any, b: any) => (a.firstName || '').localeCompare(b.firstName || ''))
                          .map((u: any) => (
                            <option key={u.id} value={u.id}>
                              {u.firstName} {u.lastName} ({u.email})
                            </option>
                          ))}
                      </select>
                      {newUserId !== editingOrder.userId && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">⚠ La orden será transferida al usuario seleccionado.</p>
                      )}
                    </div>

                    {(editingOrder.hosting?.name || editingOrder.complement?.displayName || '').toLowerCase().includes('zoho') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Cantidad</label>
                        <select
                          value={newQuantity}
                          onChange={(e) => setNewQuantity(Number(e.target.value))}
                          className="w-full border rounded-md px-3 py-2 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                        >
                          {Array.from({ length: 10 }, (_, i) => i + 1).map(n => (
                            <option key={n} value={n}>{n}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  <div className="mb-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Estado</label>
                        <select
                          value={newStatus}
                          onChange={(e) => setNewStatus(e.target.value)}
                          className="w-full border rounded-md px-3 py-2 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                        >
                          <option value="PENDING">Pendiente de Pago</option>
                          <option value="PAID">Pago</option>
                          <option value="ACTIVE">Activo</option>
                          <option value="CANCELLED">Cancelado</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Fecha de Orden</label>
                        <input
                          type="date"
                          value={newDate}
                          onChange={(e) => setNewDate(e.target.value)}
                          className="w-full border rounded-md px-3 py-2 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Dominio</label>
                      <input
                        type="text"
                        value={newDomain}
                        onChange={(e) => setNewDomain(e.target.value)}
                        placeholder="ejemplo.com"
                        className="w-full border rounded-md px-3 py-2 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Último Medio de Pago</label>
                      <select
                        value={newPaymentMethod}
                        onChange={(e) => setNewPaymentMethod(e.target.value)}
                        className="w-full border rounded-md px-3 py-2 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                      >
                        <option value="">Seleccionar...</option>
                        <option value="PAYPAL">PayPal</option>
                        <option value="PREX">Prex</option>
                        <option value="TRANSFERENCIA">Transferencia Bancaria</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Notas</label>
                    <RichEditor
                      value={newNotes}
                      onChange={setNewNotes}
                      placeholder="Notas internas de la orden..."
                    />
                  </div>

                  {/* cPanel Credentials */}
                  <div className="mb-4 p-3 bg-gray-50 dark:bg-slate-700/50 rounded-md border border-gray-100 dark:border-slate-600">
                    <label className="block text-sm font-semibold text-gray-700 mb-2 dark:text-gray-300">Credenciales cPanel</label>
                    {credentialsLoading ? (
                      <p className="text-xs text-gray-400">Cargando...</p>
                    ) : (
                      <div className="grid grid-cols-1 gap-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1 dark:text-gray-400">URL cPanel</label>
                          <input
                            type="text"
                            value={editCpanelUrl}
                            onChange={(e) => setEditCpanelUrl(e.target.value)}
                            placeholder="https://srv.example.com:2083"
                            className="w-full border rounded-md px-3 py-2 text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1 dark:text-gray-400">Usuario</label>
                            <input
                              type="text"
                              value={editCpanelUser}
                              onChange={(e) => setEditCpanelUser(e.target.value)}
                              placeholder="admin"
                              className="w-full border rounded-md px-3 py-2 text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1 dark:text-gray-400">Contraseña</label>
                            <div className="relative">
                              <input
                                type={showPassword ? 'text' : 'password'}
                                value={editCpanelPassword}
                                onChange={(e) => setEditCpanelPassword(e.target.value)}
                                placeholder="••••••••"
                                className="w-full border rounded-md px-3 py-2 pr-9 text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                              </button>
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-gray-400 dark:text-gray-500">La contraseña se cifra con AES-256 en el servidor</p>
                      </div>
                    )}
                  </div>

                  <div>
                    <Toggle
                      checked={sendEmail}
                      onChange={setSendEmail}
                      label="Enviar email de notificación al cliente"
                    />
                  </div>
                </>
              )}
            </Modal>

            {/* Delete Modal */}
            <Modal
              open={!!deletingOrder}
              onClose={() => setDeletingOrder(null)}
              icon={<Trash2 size={22} />}
              title="Eliminar Orden"
              subtitle={deletingOrder ? `${deletingOrder.hosting?.name || deletingOrder.complement?.displayName} — ${deletingOrder.user?.firstName}` : ''}
              footer={
                <>
                  <button
                    onClick={() => setDeletingOrder(null)}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md dark:text-gray-300 dark:hover:bg-slate-700"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-md"
                  >
                    Eliminar
                  </button>
                </>
              }
            >
              {deletingOrder && (
                <>
                  <p className="text-gray-600 mb-4 dark:text-gray-300">
                    ¿Estás seguro de que deseas eliminar la orden de <strong>{deletingOrder.hosting?.name || deletingOrder.complement?.displayName}</strong> para {deletingOrder.user?.firstName}? Esta acción no se puede deshacer.
                  </p>
                  <div>
                    <Toggle
                      checked={sendEmail}
                      onChange={setSendEmail}
                      label="Enviar email de notificación al cliente"
                    />
                  </div>
                </>
              )}
            </Modal>

            {/* Transfer Modal */}
            <Modal
              open={!!transferringOrder}
              onClose={() => setTransferringOrder(null)}
              icon={<UsersIcon size={22} />}
              title="Transferir Orden"
              subtitle={transferringOrder ? `${transferringOrder.hosting?.name || transferringOrder.complement?.displayName} — ${transferringOrder.user?.email}` : ''}
              footer={
                <>
                  <button
                    onClick={() => setTransferringOrder(null)}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md dark:text-gray-300 dark:hover:bg-slate-700"
                  >
                    Cancelar
                  </button>
                  <button
                    disabled={!transferUserId || transferUserId === transferringOrder?.userId}
                    onClick={async () => {
                      if (!transferUserId || transferUserId === transferringOrder?.userId) return;
                      await OrdersAPI.update(transferringOrder.id, { userId: transferUserId });
                      setTransferringOrder(null);
                      load();
                    }}
                    className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Confirmar transferencia
                  </button>
                </>
              }
            >
              {transferringOrder && (
                <>
                  <p className="text-sm text-gray-600 mb-4 dark:text-gray-300">
                    Estás por transferir la orden de{' '}
                    <strong>{transferringOrder.hosting?.name || transferringOrder.complement?.displayName}</strong>{' '}
                    del usuario actual <strong>{transferringOrder.user?.email}</strong> a otro usuario.
                  </p>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                      Nuevo propietario
                    </label>
                    <select
                      value={transferUserId}
                      onChange={(e) => setTransferUserId(e.target.value)}
                      className="w-full border-2 rounded-md px-3 py-2 dark:bg-slate-700 dark:text-white transition-colors dark:border-slate-600"
                    >
                      {users
                        .slice()
                        .sort((a: any, b: any) => (a.firstName || '').localeCompare(b.firstName || ''))
                        .map((u: any) => (
                          <option key={u.id} value={u.id}>
                            {u.firstName} {u.lastName} ({u.email})
                          </option>
                        ))}
                    </select>
                    {transferUserId && transferUserId !== transferringOrder.userId && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                        Esta orden aparecerá en el dashboard del nuevo usuario.
                      </p>
                    )}
                  </div>
                </>
              )}
            </Modal>

            {/* Create Modal */}
            <Modal
              open={creating}
              onClose={() => setCreating(false)}
              icon={<Plus size={22} />}
              title="Nueva Orden Manual"
              subtitle="Crear una orden para un cliente existente"
              footer={
                <>
                  <button
                    onClick={() => setCreating(false)}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md dark:text-gray-300 dark:hover:bg-slate-700"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleCreate}
                    disabled={!createData.userId || (createData.type === 'hosting' && !createData.hostingId) || (createData.type === 'complement' && !createData.complementId)}
                    className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Crear Orden
                  </button>
                </>
              }
            >
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Usuario</label>
                <select
                  value={createData.userId}
                  onChange={(e) => setCreateData({ ...createData, userId: e.target.value })}
                  className="w-full border rounded-md px-3 py-2 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                >
                  <option value="">Seleccionar usuario...</option>
                  {users.sort((a: any, b: any) => (a.firstName || '').localeCompare(b.firstName || '')).map((u: any) => (
                    <option key={u.id} value={u.id}>
                      {u.firstName} {u.lastName} ({u.email})
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Tipo de Orden</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="orderType"
                      checked={createData.type === 'hosting'}
                      onChange={() => setCreateData({ ...createData, type: 'hosting', complementId: '', quantity: 1 })}
                      className="dark:bg-slate-700"
                    />
                    <span className="dark:text-white">Hosting</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="orderType"
                      checked={createData.type === 'complement'}
                      onChange={() => setCreateData({ ...createData, type: 'complement', hostingId: '' })}
                      className="dark:bg-slate-700"
                    />
                    <span className="dark:text-white">Complemento</span>
                  </label>
                </div>
              </div>

              {createData.type === 'hosting' ? (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Plan de Hosting</label>
                  <select
                    value={createData.hostingId}
                    onChange={(e) => setCreateData({ ...createData, hostingId: e.target.value })}
                    className="w-full border rounded-md px-3 py-2 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                  >
                    <option value="">Seleccionar plan...</option>
                    {hostings.map((h: any) => (
                      <option key={h.id} value={h.id}>
                        {h.name} - {h.currency} {h.price} ({h.payType})
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Complemento</label>
                  <select
                    value={createData.complementId}
                    onChange={(e) => setCreateData({ ...createData, complementId: e.target.value, quantity: 1 })}
                    className="w-full border rounded-md px-3 py-2 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                  >
                    <option value="">Seleccionar complemento...</option>
                    {complements
                      .filter((c: any) => c.isVisible !== false)
                      .sort((a: any, b: any) => (a.sortOrder || 0) - (b.sortOrder || 0))
                      .map((c: any) => (
                        <option key={c.id} value={c.id}>
                          {c.displayName} - {c.currency} {c.price} ({c.payTypeDisplay || c.payType})
                        </option>
                      ))}
                  </select>
                </div>
              )}

              {createData.type === 'complement' && createData.complementId && (() => {
                const selected = complements.find((c: any) => c.id === createData.complementId)
                const isZoho = selected?.displayName?.toLowerCase().includes('zoho')
                if (isZoho) {
                  return (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Cantidad</label>
                      <select
                        value={createData.quantity}
                        onChange={(e) => setCreateData({ ...createData, quantity: Number(e.target.value) })}
                        className="w-full border rounded-md px-3 py-2 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                      >
                        {Array.from({ length: 10 }, (_, i) => i + 1).map(n => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </div>
                  )
                }
                return null
              })()}

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Dominio</label>
                  <input
                    type="text"
                    value={createData.domain}
                    onChange={(e) => setCreateData({ ...createData, domain: e.target.value })}
                    placeholder="ejemplo.com"
                    className="w-full border rounded-md px-3 py-2 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Último Medio de Pago</label>
                  <select
                    value={createData.lastPaymentMethod}
                    onChange={(e) => setCreateData({ ...createData, lastPaymentMethod: e.target.value })}
                    className="w-full border rounded-md px-3 py-2 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                  >
                    <option value="">Seleccionar...</option>
                    <option value="PAYPAL">PayPal</option>
                    <option value="PREX">Prex</option>
                    <option value="TRANSFERENCIA">Transferencia Bancaria</option>
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Estado</label>
                    <select
                      value={createData.status}
                      onChange={(e) => setCreateData({ ...createData, status: e.target.value })}
                      className="w-full border rounded-md px-3 py-2 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    >
                      <option value="PENDING">Pendiente de Pago</option>
                      <option value="PAID">Pago</option>
                      <option value="ACTIVE">Activo</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Fecha</label>
                    <input
                      type="date"
                      value={createData.date}
                      onChange={(e) => setCreateData({ ...createData, date: e.target.value })}
                      className="w-full border rounded-md px-3 py-2 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Toggle
                  checked={createData.sendEmail}
                  onChange={(checked: boolean) => setCreateData({ ...createData, sendEmail: checked })}
                  label="Enviar email de notificación al cliente"
                />
              </div>
            </Modal>
          </div>
        )}
        {!loading && (
          <Pagination
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalItems={filteredTotal}
            className="bg-gray-50 dark:bg-slate-900/50 rounded-b-xl"
          />
        )}
      </div>
    </div>
  )
}

export default AdminOrders
